import { bridgeAgentControllerConversationRef, buildAdapterAgentControllerConversationRef, resolveBridgedAgentControllerConversationId, } from "./conversation-bridge.js";
export { bridgeAgentControllerConversationRef, buildAdapterAgentControllerConversationRef, resolveBridgedAgentControllerConversationId, resolveBuiltinAdapterConversationBridgeSpec, } from "./conversation-bridge.js";
export { buildCliLaunchInvocation, buildExecutableLaunchContract, materializeCliLaunchCommand, resolveExecutableOnPath, resolveExecutablePathsOnPath, resolveInstalledCommandPath, resolveInstalledCommandPaths, resolveInstalledNodeScriptLaunchCandidates, resolveInstalledNodeScriptLaunchContract, resolveSiblingCommandPath, verifyCliLaunchContract, verifyLaunchContractFiles, } from "./launch-contract.js";
export { buildProviderExitError, classifyCliDriverTimeoutFailure, } from "./runtime-failure.js";
export { classifyRuntimePermissionDenialMessage, looksLikeRuntimePermissionDenialMessage, RUNTIME_PERMISSION_DENIED_REASON_CODE, readRuntimePermissionDenialDetails, } from "./runtime-permission-denial.js";
export { ADAPTER_TOOL_ACTIVITY_NAMES, resolveAdapterToolActivityName, resolveAdapterToolWorkActivity, } from "./tool-activity.js";
export const DEFAULT_GRACEFUL_INTERRUPT_TIMEOUT_MS = 10_000;
const DEFAULT_INTERRUPT_REASON = "service.interrupt";
const DEFAULT_CANCEL_REASON = "service.cancel";
const TEST_DISABLE_DEFAULT_INVOKE = Symbol.for("wakeloop.test.disableDefaultInvoke");
export const createSharedDispatchShell = (input) => {
    const bridgeSpec = input.bridgeSpec ?? null;
    return async (dispatchInput) => {
        const transportMode = dispatchInput.transportMode ?? "local-cli";
        const transportInvoke = input.invokeByMode?.[transportMode];
        if (input.invokeByMode && !transportInvoke) {
            return {
                errorCode: "adapter.unreachable",
                errorMessage: `adapter.unreachable: ${input.adapterId} transport '${transportMode}' is not configured`,
                errorType: "adapter",
                status: "failed",
            };
        }
        const defaultInvoke = isDefaultInvokeDisabledForTests(input.runtimeOptions)
            ? null
            : (input.resolveDefaultInvoke() ?? null);
        const resolvedInvoke = transportInvoke ?? input.invoke ?? defaultInvoke;
        if (!resolvedInvoke) {
            return {
                errorCode: "controller.runtime.missing",
                errorMessage: `controller.runtime.missing: ${input.adapterId} invoke runtime is unavailable`,
                errorType: "adapter",
                status: "failed",
            };
        }
        try {
            const invokeRequest = input.resolveInvokeRequest?.({
                bridgeSpec,
                request: dispatchInput,
            }) ??
                buildDefaultInvokeRequest({
                    bridgeSpec,
                    request: dispatchInput,
                });
            const result = await resolvedInvoke(invokeRequest);
            const contextDelta = input.resolveSuccessContextDelta?.({
                bridgeSpec,
                invokeRequest,
                request: dispatchInput,
                result,
            }) ??
                resolveCanonicalContextDelta({
                    bridgeSpec,
                    contextDelta: result.contextDelta,
                });
            return {
                ...(contextDelta ? { contextDelta } : {}),
                output: result.output,
                status: "success",
                ...(result.telemetry ? { telemetry: result.telemetry } : {}),
            };
        }
        catch (error) {
            return toAdapterDispatchFailure(error, "adapter");
        }
    };
};
export const createSharedControlShell = (input) => ({
    cancel: async (requestInput) => {
        const request = buildAdapterCancelRequest(requestInput);
        const sendCancelSignal = resolveCancelSignalSender({
            sendCancel: input.sendCancel,
            sendInterrupt: input.sendInterrupt,
        });
        if (!sendCancelSignal) {
            return buildUnsupportedCancelResult({
                taskId: request.taskId,
            });
        }
        try {
            await sendCancelSignal.send(request);
            return {
                accepted: true,
                deliveryPath: sendCancelSignal.deliveryPath,
                reasonCode: "accepted",
                signalDelivery: "sent",
                taskId: request.taskId,
            };
        }
        catch (error) {
            const reasonCode = resolveControlReasonCode(error) ?? "signal.send_failed";
            return buildUnsupportedCancelResult({
                reasonCode,
                reasonDetail: resolveControlReasonDetail(error, reasonCode),
                taskId: request.taskId,
            });
        }
    },
    interrupt: async (requestInput) => {
        const plan = buildInterruptPlan({
            gracefulTimeoutMs: requestInput.gracefulTimeoutMs ?? input.gracefulTimeoutMs,
            mode: requestInput.mode,
            reason: requestInput.reason,
            taskId: requestInput.taskId,
        });
        if (!input.sendInterrupt) {
            return buildUnsupportedInterruptResult({
                plan,
                reasonCode: "adapter.unsupported",
                taskId: requestInput.taskId,
            });
        }
        try {
            await executeInterruptPlan({
                plan,
                sendSignal: input.sendInterrupt,
                waitForMs: input.waitForMs,
            });
            return {
                accepted: true,
                plan,
                reasonCode: "accepted",
                signalDelivery: "sent",
                taskId: requestInput.taskId,
            };
        }
        catch (error) {
            const reasonCode = resolveControlReasonCode(error) ?? "signal.send_failed";
            return buildUnsupportedInterruptResult({
                plan,
                reasonCode,
                reasonDetail: resolveControlReasonDetail(error, reasonCode),
                taskId: requestInput.taskId,
            });
        }
    },
});
export const invokeWithSingleContextHeal = async (input) => {
    try {
        const first = await input.invokeOnce(input.contextId);
        return {
            contextHealed: false,
            contextId: first.contextId,
            output: first.output,
            telemetry: first.telemetry,
        };
    }
    catch (firstError) {
        if (!(input.contextId && input.isContextLostError(firstError))) {
            throw firstError;
        }
        await input.onContextLost?.();
        try {
            const healed = await input.invokeOnce(null);
            return {
                contextHealed: true,
                contextId: healed.contextId,
                output: healed.output,
                telemetry: healed.telemetry
                    ? {
                        ...healed.telemetry,
                        contextHealed: true,
                        resumedContext: input.contextId !== null,
                    }
                    : undefined,
            };
        }
        catch (healError) {
            throw input.createRebuildFailedError(healError);
        }
    }
};
export const buildInterruptPlan = (input) => {
    const mode = input.mode ?? "graceful";
    const reason = input.reason ?? DEFAULT_INTERRUPT_REASON;
    if (mode === "force") {
        return {
            initial: {
                mode: "force",
                reason,
                taskId: input.taskId,
            },
        };
    }
    const delayMs = normalizeGracefulTimeoutMs(input.gracefulTimeoutMs);
    return {
        initial: {
            mode: "graceful",
            reason,
            taskId: input.taskId,
        },
        escalation: {
            delayMs,
            mode: "force",
            reason,
            taskId: input.taskId,
        },
    };
};
export const executeInterruptPlan = async (input) => {
    await input.sendSignal(input.plan.initial);
    if (!input.plan.escalation) {
        return;
    }
    const waitForMs = input.waitForMs ?? defaultWaitForMs;
    await waitForMs(input.plan.escalation.delayMs);
    await input.sendSignal({
        mode: input.plan.escalation.mode,
        reason: input.plan.escalation.reason,
        taskId: input.plan.escalation.taskId,
    });
};
export const buildUnsupportedInterruptResult = (input) => ({
    accepted: false,
    plan: input.plan,
    reasonCode: input.reasonCode ?? "adapter.unsupported",
    ...(input.reasonDetail ? { reasonDetail: input.reasonDetail } : {}),
    signalDelivery: "not_sent",
    taskId: input.taskId,
});
export const buildUnsupportedCancelResult = (input) => ({
    accepted: false,
    reasonCode: input.reasonCode ?? "adapter.unsupported",
    ...(input.reasonDetail ? { reasonDetail: input.reasonDetail } : {}),
    signalDelivery: "not_sent",
    taskId: input.taskId,
});
export const buildAdapterCancelRequest = (input) => ({
    reason: normalizeControlReason(input.reason, DEFAULT_CANCEL_REASON),
    taskId: input.taskId,
});
export const toAdapterDispatchFailure = (error, defaultErrorType = "adapter") => {
    const errorCode = extractErrorCode(error);
    const errorType = extractErrorType(error) ?? defaultErrorType;
    const providerAccessEvidence = extractProviderAccessEvidence(error);
    return {
        ...(errorCode ? { errorCode } : {}),
        errorMessage: error instanceof Error ? error.message : "adapter dispatch failed",
        errorType,
        ...(providerAccessEvidence ? { providerAccessEvidence } : {}),
        status: "failed",
    };
};
export const resolveAdapterAgentContext = (input) => {
    const env = input.env ?? {};
    const rawValue = env[input.spec.sourceField];
    const agentControllerConversationId = typeof rawValue === "string" && rawValue.trim().length > 0
        ? rawValue.trim()
        : null;
    const agentControllerConversation = input.bridgeSpec && agentControllerConversationId
        ? buildAdapterAgentControllerConversationRef({
            agentControllerRef: input.bridgeSpec.agentControllerRef,
            agentControllerConversationId,
            agentControllerConversationKind: input.bridgeSpec.agentControllerConversationKind,
        })
        : null;
    return {
        agentControllerConversationId,
        sourceField: input.spec.sourceField,
        mappingVersion: input.spec.mappingVersion,
        ...(agentControllerConversation ? { agentControllerConversation } : {}),
    };
};
const defaultWaitForMs = async (delayMs) => {
    if (delayMs <= 0) {
        return;
    }
    await new Promise((resolve) => {
        setTimeout(() => {
            resolve();
        }, delayMs);
    });
};
const normalizeGracefulTimeoutMs = (gracefulTimeoutMs) => {
    if (!Number.isInteger(gracefulTimeoutMs) || gracefulTimeoutMs === undefined) {
        return DEFAULT_GRACEFUL_INTERRUPT_TIMEOUT_MS;
    }
    return gracefulTimeoutMs > 0
        ? gracefulTimeoutMs
        : DEFAULT_GRACEFUL_INTERRUPT_TIMEOUT_MS;
};
const extractErrorCode = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const code = error.code;
    if (typeof code !== "string") {
        return null;
    }
    const normalized = code.trim();
    return normalized.length > 0 ? normalized : null;
};
const extractErrorType = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const errorType = error.errorType;
    if (typeof errorType !== "string") {
        return null;
    }
    const normalized = errorType.trim();
    switch (normalized) {
        case "adapter":
            return "adapter";
        case "auth":
            return "auth";
        case "config":
            return "config";
        case "timeout":
            return "timeout";
        case "network":
            return "network";
        case "unknown":
            return "unknown";
        default:
            return null;
    }
};
const extractProviderAccessEvidence = (error) => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const evidence = error
        .providerAccessEvidence;
    if (!evidence || typeof evidence !== "object") {
        return null;
    }
    const candidate = evidence;
    if (candidate.provider !== "claude-code" ||
        candidate.source !== "wake_failure") {
        return null;
    }
    if (candidate.reasonCode !== "provider_account.organization_access_disabled" &&
        candidate.reasonCode !== "provider_account.subscription_access_unavailable") {
        return null;
    }
    const observedAtMs = candidate.observedAtMs;
    if (typeof observedAtMs !== "number" ||
        !Number.isInteger(observedAtMs) ||
        observedAtMs < 0) {
        return null;
    }
    return {
        observedAtMs,
        provider: candidate.provider,
        reasonCode: candidate.reasonCode,
        source: candidate.source,
    };
};
const normalizeControlReason = (reason, fallback) => {
    if (typeof reason !== "string") {
        return fallback;
    }
    const normalized = reason.trim();
    return normalized.length > 0 ? normalized : fallback;
};
const resolveCanonicalContextDelta = (input) => {
    if (!input.contextDelta) {
        return undefined;
    }
    const canonicalContextDelta = {
        agentControllerConversationId: input.contextDelta.agentControllerConversationId,
        ...(input.contextDelta.refreshed === true ? { refreshed: true } : {}),
        ...(input.contextDelta.agentControllerConversation
            ? {
                agentControllerConversation: input.contextDelta.agentControllerConversation,
            }
            : {}),
    };
    if (!input.bridgeSpec) {
        return canonicalContextDelta;
    }
    const agentControllerConversation = bridgeAgentControllerConversationRef({
        agentControllerConversationId: canonicalContextDelta.agentControllerConversationId,
        bridgeSpec: input.bridgeSpec,
        agentControllerConversation: canonicalContextDelta.agentControllerConversation,
    });
    return {
        ...canonicalContextDelta,
        ...(agentControllerConversation ? { agentControllerConversation } : {}),
    };
};
const buildDefaultInvokeRequest = (input) => {
    const agentControllerConversationId = input.bridgeSpec
        ? resolveBridgedAgentControllerConversationId({
            agentControllerConversationId: input.request.agentControllerConversationId,
            bridgeSpec: input.bridgeSpec,
            agentControllerConversation: input.request.agentControllerConversation,
        })
        : normalizeOptionalText(input.request.agentControllerConversationId);
    return {
        ...input.request,
        agentControllerConversationId,
    };
};
const normalizeOptionalText = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
};
const isDefaultInvokeDisabledForTests = (runtimeOptions) => runtimeOptions?.[TEST_DISABLE_DEFAULT_INVOKE] === true;
const resolveCancelSignalSender = (input) => {
    if (input.sendCancel) {
        return {
            deliveryPath: "cancel",
            send: input.sendCancel,
        };
    }
    if (!input.sendInterrupt) {
        return null;
    }
    return {
        deliveryPath: "force_interrupt_fallback",
        send: async (request) => {
            await input.sendInterrupt?.({
                mode: "force",
                reason: request.reason ?? DEFAULT_CANCEL_REASON,
                taskId: request.taskId,
            });
        },
    };
};
const resolveControlReasonCode = (error) => {
    const code = extractErrorCode(error);
    switch (code) {
        case "adapter.unsupported":
        case "signal.not_sent":
        case "signal.send_failed":
        case "task.not_found":
        case "task.not_running":
            return code;
        default:
            return null;
    }
};
const toUnknownErrorMessage = (error) => error instanceof Error ? error.message : "unknown adapter control error";
const resolveControlReasonDetail = (error, reasonCode) => reasonCode === "adapter.unsupported"
    ? undefined
    : toUnknownErrorMessage(error);
